import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { SetterOrUpdater, useSetRecoilState } from "recoil";

import {
  ConflictErrorSeverity,
  IConflict,
  IExtendableDto,
  Log,
  passengersListBookingAtom,
  simpleResultFalse,
  simpleResultTrue,
  updateListEntity,
  useDrawers,
} from "@ryde/shared";

import { Expanded, ExpandedTable } from "../common";
import { ConflictsNav } from "./conflicts-nav";
import { ConflictsTableCell } from "./conflicts-table-cell";

interface IConflictsProps {
  title: string;
  conflicts: IConflict[];
  loadProductionPreview: () => Promise<void>;
  headers: (keyof IConflict)[];
  /** Severity for nav styling: critical (red) or warning (orange). Default critical. */
  conflictNavSeverity?: ConflictErrorSeverity;
}

export const Conflicts: FC<IConflictsProps> = ({
  title,
  conflicts,
  headers,
  loadProductionPreview,
  conflictNavSeverity,
}) => {
  const { openEditFormDrawer } = useDrawers();
  const setPassengersListBookingAtom = useSetRecoilState(
    passengersListBookingAtom,
  );

  const { t } = useTranslation();

  const onBookingEdit = (conflict: IConflict) => async (): Promise<void> => {
    if (!conflict.bookingId) {
      return;
    }

    const bookingId = conflict.bookingId;
    const simpleResult = await openEditFormDrawer({
      entity: "booking",
      id: bookingId,
      canResolve: savedDto => (savedDto ? simpleResultTrue : simpleResultFalse),
    });

    if (!simpleResult.success) {
      Log.warn(t("errors.bookingDidntUpdate", { bookingId: bookingId }));
      return;
    }

    loadProductionPreview();
  };

  const onPassengerEdit = (conflict: IConflict) => async (): Promise<void> => {
    if (!conflict.passengerId) {
      return;
    }

    const passengerId = conflict.passengerId;
    const simpleResult = await openEditFormDrawer({
      entity: "passenger",
      id: passengerId,
      canResolve: savedDto => (savedDto ? simpleResultTrue : simpleResultFalse),
    });

    if (!simpleResult.success) {
      Log.warn(t("errors.passengerDidntUpdate", { passengerId }));
      return;
    }

    updateListEntity(
      setPassengersListBookingAtom as unknown as SetterOrUpdater<
        IExtendableDto[]
      >,
      simpleResult.data as IExtendableDto,
    );
  };

  const onStationEdit = (conflict: IConflict) => async (): Promise<void> => {
    if (!conflict.stationId) {
      return;
    }

    const stationId = conflict.stationId;
    const simpleResult = await openEditFormDrawer({
      entity: "station",
      id: stationId,
      canResolve: savedDto => (savedDto ? simpleResultTrue : simpleResultFalse),
    });

    if (simpleResult.success) {
      // FOR CHECKING ANOTHER CONFLICTS
      loadProductionPreview();
    } else {
      Log.warn(t("errors.stationDidntUpdate", { stationId }));
      return;
    }
  };

  return (
    <Expanded
      NavControl={
        <ConflictsNav
          title={title}
          conflictNumber={conflicts.length}
          severity={conflictNavSeverity}
        />
      }
    >
      <ExpandedTable dataE2E="conflicts-table">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map(headerKey => (
                <TableCell key={headerKey}>
                  {t(`booking.formLabel.conflictsDetails.${headerKey}`)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {conflicts.map((conflictRow, i) => (
              <TableRow
                key={`${conflictRow.passengerId}_${i}`}
                data-e2e="conflict-table-row"
              >
                {headers.map((headerKey, j) => {
                  const conflictValue = conflictRow[headerKey];
                  return (
                    <ConflictsTableCell
                      key={`${conflictRow.passengerId}_${conflictValue}_${j}`}
                      conflictKey={headerKey}
                      conflictValue={conflictValue}
                      isBooking={conflictRow.bookingId !== undefined}
                      isPassenger={conflictRow.passengerId !== undefined}
                      isStationName={conflictRow.stationName !== undefined}
                      onBookingEdit={onBookingEdit(conflictRow)}
                      onPassengerEdit={onPassengerEdit(conflictRow)}
                      onStationEdit={onStationEdit(conflictRow)}
                    />
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ExpandedTable>
    </Expanded>
  );
};
